import {
    Component,
    OnInit,
    OnChanges,
    SimpleChanges,
    OnDestroy,
    Input,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ViewChild,
    ElementRef,
    Output,
    EventEmitter,
    Renderer2,
    AfterViewInit,
    ViewChildren,
    QueryList,
    HostListener,
} from '@angular/core';

import { SubNavService } from './sub-nav.shared.service';
import { ISubNavPage } from './sub-nav.shared.interface';
import { Subscription, merge, fromEvent } from 'rxjs';
import { map, filter, auditTime, take, withLatestFrom, delay } from 'rxjs/operators';
import { OnlineMenuController } from '@shared/core/controllers/onlineMenu/online-menu.controller';
import { RoutesController } from '@shared/core/controllers/routes/routes.controller';

@Component({
    template: '<div>This component requires extending</div>',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubNavComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('host', { static: true }) public host!: ElementRef;
    @ViewChild('indicator', { static: true }) public indicator!: ElementRef;
    @ViewChild('items', { static: true }) public items!: ElementRef;
    @ViewChildren('liElems') public liElems!: QueryList<ElementRef<HTMLLIElement>>;

    @Output() public onSelected: EventEmitter<Event> = new EventEmitter();
    @Output() public onPageClicked: EventEmitter<ISubNavPage> = new EventEmitter();
    @Input() public registryId!: string;
    @Input() public pages!: ISubNavPage[]; /* For displaying categories and products */
    @Input() public iconSize: OLO.Components.BUTTON_SIZE = 'sm';
    @Input() public scroll: boolean = true;
    @Input() public activeId: number = null;
    @Input() public return: boolean = true;
    @Input() public delayScroll: number = null;
    @Input() public relativeTarget!: HTMLElement;

    public activeLabel!: string;
    public activeListener$!: Subscription;
    public windowEventsListener$!: Subscription;
    public forceHeightUpdateListener$!: Subscription;

    private _queryParamsScrollTriggered: boolean = false;
    private _elem!: HTMLElement;
    private _init: boolean = false;
    private _overflowPages: ISubNavPage[] = [];
    private _resizeTimeout: number;

    @HostListener('window:resize') public onWindowResize(): void {
        if (this._resizeTimeout) {
            clearTimeout(this._resizeTimeout);
            this._resizeTimeout = null;
        }
        this._overflowPages = [];
        this.changeDetectorRef.markForCheck();
        this._resizeTimeout = window.setTimeout(() => {
            this._recalculateItemsWidthContainer();
        }, 50);
    }

    constructor(
        public changeDetectorRef: ChangeDetectorRef,
        public subNavService: SubNavService,
        public renderer2: Renderer2,
        private readonly _onlineMenuController: OnlineMenuController,
        private readonly _routesController: RoutesController,
    ) {}

    ngOnInit(): void {
        if (!this.registryId) {
            console.warn('SubNavComponent [registryId] not provided');
        }

        this._elem = this.host?.nativeElement;

        if (this.registryId) {
            this.activeListener$ = this.subNavService.currentTargets$
                .pipe(
                    map((obj) => obj[this.registryId]),
                    filter((obj) => !!obj === true),
                )
                .subscribe((active) => {
                    this.activeId = active;
                    this._findLabel();

                    this.changeDetectorRef.detectChanges();
                });
        }

        if (this.scroll) {
            /* Set position on event change */
            this.windowEventsListener$ = merge(fromEvent(window, 'scroll'), fromEvent(window, 'resize'))
                .pipe(
                    filter(() => this.subNavService.isScrolling === false),
                    auditTime(200),
                )
                .subscribe(() => {
                    this._setContainerHeight();
                    this._activateElement();
                });
        }

        this.forceHeightUpdateListener$ = this.subNavService.heightUpdate$.subscribe(() => {
            this._setContainerHeight();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this._recalculateItemsWidthContainer();
        if (changes.pages && changes.pages.previousValue !== this.pages && this.pages) {
            this._findLabel();
            this.changeDetectorRef.markForCheck();
        }

        if (changes.activeId && changes.activeId.previousValue !== this.activeId && this.pages) {
            this._findLabel();
            this.changeDetectorRef.markForCheck();
        }

        if (this.pages && this._init) {
            this._handleScrollFromParam();
        }
    }

    ngAfterViewInit(): void {
        this._init = true;
        this._recalculateItemsWidthContainer();

        if (this.items) {
            this.items.nativeElement.parentElement.addEventListener('scroll', this.handleInnerScroll);
        }

        this._setContainerHeight();
        this._setIndicatorPosition();
    }

    ngOnDestroy(): void {
        this._init = false;

        if (this.activeListener$ && !this.activeListener$.closed) this.activeListener$.unsubscribe();

        if (this.windowEventsListener$ && !this.windowEventsListener$.closed) this.windowEventsListener$.unsubscribe();

        if (this.items) {
            (this.items.nativeElement.parentElement as HTMLElement).removeEventListener('scroll', this.handleInnerScroll);
        }
    }

    public handleInnerScroll($event: Event): void {
        $event.stopImmediatePropagation();
        $event.stopPropagation();
    }

    public onItemClick($event: Event, page: ISubNavPage, index: number = null): void {
        if (page.Id === this.activeId && !this.isMobile) return;

        this.onSelected.emit($event);
        this.onPageClicked.emit(page);

        if (this.scroll && !page.Url) {
            setTimeout(() => {
                this.subNavService.globalScrollTo(this.registryId, page.Id, index, this.host?.nativeElement);
            }, this.delayScroll || 0);
        }

        if (page.Url && !this.scroll) {
            this.subNavService.navToUrl(page.Url);
        }
    }

    public trackBy(index: number, page: ISubNavPage): number {
        return page?.Id || index;
    }

    public get isMobile(): boolean {
        return window.innerWidth < 768;
    }

    public get overflowPages(): Array<ISubNavPage> {
        return this._overflowPages;
    }

    public get showMoreForDesktop(): boolean {
        return this.isMobile === false && this.overflowPages.length > 0;
    }

    public get selectedPage(): ISubNavPage {
        return (this.activeId && this.pages?.find((obj) => obj.Id === this.activeId)) || null;
    }

    public get totalCategories(): number {
        return this.pages?.length || 0;
    }

    public get badgeType(): OLO.Components.BADGE_TYPE {
        return this.totalCategories > 0 ? 'primary' : 'primary';
    }

    public handleDropDownSelected($event: OLO.Components.DropDown.IDropDownOption): void {
        const index: number = this.pages.indexOf($event);
        this.onItemClick(null, $event, index);
    }

    private _handleScrollFromParam(): void {
        if (this._queryParamsScrollTriggered) return;

        this._queryParamsScrollTriggered = true;

        this._onlineMenuController
            .getCurrentOnlineMenuId$()
            .pipe(
                filter((onlineMenuId) => Boolean(onlineMenuId)),
                withLatestFrom(this._routesController.getCurrentRouteQueryParams$()),
                delay(200),
                take(1),
            )
            .subscribe(([currentMenuId, params]) => {
                if (params?.pageId && params?.menuId && +params.menuId === currentMenuId) {
                    const index = this.pages.findIndex((obj) => obj.Id === +params?.pageId) || null;
                    this.subNavService.globalScrollTo(this.registryId, +params?.pageId, index, this.host?.nativeElement);
                }
            });
    }

    private _setIndicatorPosition(): void {
        if (this.isMobile || !this._elem) return;
        setTimeout(() => {
            /* Delay is required to handle boldness change and little size change */
            const items: HTMLUListElement = this.items ? this.items.nativeElement : null;
            const elem: HTMLLIElement = this.indicator ? this.indicator.nativeElement : null;

            if (!items || !elem) return;

            const activeLi: HTMLLIElement = items.querySelector(`#sub-nav-${this.registryId}-${this.activeId}`);
            if (!activeLi) return;

            const leftOffset: number = activeLi.offsetLeft;
            const width: number = activeLi.clientWidth;

            this.renderer2.setStyle(elem, 'transition', 'all .3s ease');
            this.renderer2.setStyle(elem, 'position', 'absolute');
            this.renderer2.setStyle(elem, 'bottom', '0');
            this.renderer2.setStyle(elem, 'left', `${leftOffset}px`);
            this.renderer2.setStyle(elem, 'width', `${width}px`);
        }, 10);
    }

    private _findLabel(): void {
        if (this.pages) {
            const page: ISubNavPage = this.pages?.find((obj) => obj.Id === this.activeId);

            if (page) {
                this.activeLabel = page.Name;

                this._setIndicatorPosition();
            }
        }
    }

    private _setContainerHeight(): void {
        if (this.isMobile && this.items) {
            const relTarget: HTMLDivElement = this.relativeTarget || this.items.nativeElement.parentElement.parentElement;
            const rects: DOMRect = relTarget.getBoundingClientRect();
            const availableHeight: number = window.innerHeight - (rects.top + relTarget.clientHeight);
            this.renderer2.setStyle(this.items.nativeElement.parentElement, 'height', `${availableHeight + 5}px`);
            this.renderer2.setStyle(this.items.nativeElement.parentElement, 'overflow-y', 'scroll');

            this.changeDetectorRef.markForCheck();
        }
    }

    private _recalculateItemsWidthContainer(): void {
        if (this.isMobile || !this._init || !this.items || !this.items.nativeElement || !this.liElems || !this._elem) return;

        setTimeout(() => {
            this._overflowPages = [];
            const totalULWidth: number = this.items.nativeElement.parentElement.clientWidth;

            this.liElems?.reduce((acc, ref) => {
                const e: HTMLLIElement = ref.nativeElement;
                const style: CSSStyleDeclaration = window.getComputedStyle(e);
                const marginLeft: number = parseInt(style.marginLeft, 10) || 0;
                const marginRight: number = parseInt(style.marginRight, 10) || 0;
                const total: number = acc + e.clientWidth + marginLeft + marginRight;

                if (total + 100 > totalULWidth) {
                    const extractId: string[] = e.id?.split('-');
                    if (extractId.length) {
                        const page: ISubNavPage = this.pages?.find((obj) => obj.Id === +extractId[extractId.length - 1]);
                        if (page) {
                            this._overflowPages.push(page);
                            this.renderer2.addClass(e, 'is-overflowing');
                        }
                    }
                } else {
                    this.renderer2.removeClass(e, 'is-overflowing');
                }

                return total;
            }, 0);
            this.changeDetectorRef.markForCheck();
        }, 0);

        this._activateElement();
    }

    private _activateElement(): void {
        const hostBottomEdge: number = (this._elem && this._elem.getClientRects()[0].top + this._elem.clientHeight) || 0;
        const threshold: number = this.subNavService.activationThreshold;

        let foundCurrentTargetAreaId: number;

        /* Find current scrolling area comparing top and bottom edge */
        if (!this.subNavService.targetRegistry[this.registryId]) return;

        this.subNavService.targetRegistry[this.registryId].forEach((value, key) => {
            if (foundCurrentTargetAreaId) return;

            const rects: ClientRect = value.getClientRects()[0];
            if (!rects) return;

            const edgeTop: number = rects.top;
            const edgeBottom: number = rects.top + value.clientHeight;

            const isHostBelowTopEdge: boolean = edgeTop - threshold < hostBottomEdge;
            const isHostAboveElemsBottomEdge: boolean = edgeBottom - threshold > hostBottomEdge;
            if (isHostBelowTopEdge && isHostAboveElemsBottomEdge) {
                foundCurrentTargetAreaId = key;
            }
        });

        if (foundCurrentTargetAreaId && this.activeId !== foundCurrentTargetAreaId) {
            this.subNavService.setCurrent(this.registryId, foundCurrentTargetAreaId);
        }
    }
}
